import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * The homeroom an attendance page is pointed at.
 *
 * The same idea as `courseScopeStore`, for the other half of the shell: which
 * class you are grading is session context, and so is which homeroom you are
 * taking the register for. Neither belongs to a page, so both live on the
 * topbar and both are remembered across a reload.
 *
 * Kept separate from the course scope rather than folded into it because the
 * two scopes are never on screen together — attendance is homeroom-and-day
 * scoped, the gradebook is course-and-term scoped — and a shared `scopeCount`
 * would put a Course pill above the register.
 *
 * Held as an id rather than an object, for the reason the course scope is: an
 * id survives a reload without going stale. It is resolved against the freshly
 * fetched list on every render (`useHomeroomScope`), so an id belonging to
 * another school, another year, or a homeroom since renamed falls back to the
 * first of the list rather than showing a register that is not there.
 */
interface HomeroomScopeState {
  /** The homeroom the user last chose, or null to follow the first on the list. */
  selectedHomeroomId: string | null;
  setSelectedHomeroomId: (id: string | null) => void;

  /**
   * How many pages on screen are scoped to a homeroom.
   *
   * A count rather than a flag, for the reason `courseScopeStore` documents:
   * React mounts effects twice under StrictMode, and a flag would be switched
   * off by the first unmount of a page that is still on screen. Deliberately
   * not persisted — it describes what is rendered, not what the user chose.
   */
  scopeCount: number;
  acquireScope: () => void;
  releaseScope: () => void;
}

export const useHomeroomScopeStore = create<HomeroomScopeState>()(
  persist(
    (set) => ({
      selectedHomeroomId: null,
      setSelectedHomeroomId: (selectedHomeroomId) => set({ selectedHomeroomId }),
      scopeCount: 0,
      acquireScope: () => set((state) => ({ scopeCount: state.scopeCount + 1 })),
      releaseScope: () =>
        set((state) => ({ scopeCount: Math.max(0, state.scopeCount - 1) })),
    }),
    {
      name: 'homeroom-scope-storage',
      partialize: ({ selectedHomeroomId }) => ({ selectedHomeroomId }),
    }
  )
);
